import { Modal } from "@general-backoffice/core/index";
import moment from "moment";
import React, { type ComponentType, type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "reactstrap";

import type { AccountMovement } from "../../api";
import getCurrencySymbol from "../../utils/getCurrencySymbol";
import { TypedTable } from "../../utils/typedLib";
import useModal from "../hooks/useModal";

interface AccountMovementsModalProps {
  isOpen?: boolean;
  close?: () => void;
  accountMovements?: AccountMovement[] | null;
  currency?: string;
}

const AccountMovementsModal: FC<AccountMovementsModalProps> = ({
  isOpen = false,
  close = () => {},
  accountMovements = [],
  currency,
}) => {
  const { t } = useTranslation("bots", { keyPrefix: "accountMovementsModal" });
  const symbol = useMemo(() => getCurrencySymbol(currency), [currency]);

  // Filter only balance movements (deposits and withdrawals)
  const balanceMovements = useMemo(() => {
    return (accountMovements || [])
      .filter(({ type }) => type === "DEAL_TYPE_BALANCE")
      .sort((a, b) => new Date(b.time || b.createdAt || 0).getTime() - new Date(a.time || a.createdAt || 0).getTime());
  }, [accountMovements]);

  const columns = [
    {
      name: t("headers.date"),
      formattedContent: (row: AccountMovement) => {
        const movement = row;
        const timestamp = movement.time || movement.createdAt;
        const mm = moment(timestamp);
        return (
          <div className="my--3">
            <div className="font-weight-500">{mm.format("HH:mm:ss").split(":").join(" : ")}</div>
            <small>{mm.format("L")}</small>
          </div>
        );
      },
    },
    {
      name: t("headers.type"),
      formattedContent: (row: AccountMovement) => {
        const { amount } = row;
        return (
          <Badge color={amount > 0 ? "success" : "danger"}>
            {amount > 0 ? t("types.deposit") : t("types.withdrawal")}
          </Badge>
        );
      },
    },
    {
      name: t("headers.amount"),
      formattedContent: (row: AccountMovement) => {
        const { amount } = row;
        return (
          <span className={amount > 0 ? "text-success" : "text-danger"}>
            {amount > 0 ? "+" : ""}
            {amount?.toFixed(2)} {symbol}
          </span>
        );
      },
    },
  ];

  // Calculate totals
  const totals = useMemo(() => {
    return balanceMovements.reduce(
      (acc, { amount }) => {
        if (amount > 0) acc.deposit += amount;
        if (amount < 0) acc.withdrawal += Math.abs(amount);
        return acc;
      },
      { deposit: 0, withdrawal: 0 }
    );
  }, [balanceMovements]);

  return (
    <Modal.Simple
      isOpen={isOpen}
      toggleOpen={close}
      title={t("title")}
      size="lg"
      buttons={[
        {
          children: t("closeButton"),
          onClick: close,
        },
      ]}
    >
      <div className="mb-3 d-flex justify-content-around">
        <div className="text-center">
          <small className="text-muted">{t("totalDeposits")}</small>
          <div className="h4 text-success mb-0">
            +{totals.deposit.toFixed(2)} {symbol}
          </div>
        </div>
        <div className="text-center">
          <small className="text-muted">{t("totalWithdrawals")}</small>
          <div className="h4 text-danger mb-0">
            -{totals.withdrawal.toFixed(2)} {symbol}
          </div>
        </div>
      </div>
      <TypedTable
        columns={columns}
        rows={balanceMovements}
        noContentMessage={t("empty")}
        rowHeight={60}
        rowAmount={10}
        selectable={false}
        sortable={false}
      />
    </Modal.Simple>
  );
};

interface UseAccountMovementsModalOptions {
  autoOpen?: boolean;
  onClose?: () => void;
}

interface UseAccountMovementsModalReturn {
  Modal: ComponentType<Omit<AccountMovementsModalProps, "isOpen" | "close">>;
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

export const useAccountMovementsModal = (options?: UseAccountMovementsModalOptions): UseAccountMovementsModalReturn =>
  useModal({ Modal: AccountMovementsModal, ...options });

export default AccountMovementsModal;
